import { cn } from '@attendance/ui/utils';
import { collapsedClasses } from './railScope.js';

/* ===========================================================================
 * THE RAIL'S SKIN — the icon scale, the inset, the row, the eyebrow.
 *
 * Every value the sidebar draws itself with, in one place and out of
 * `Sidebar.tsx`, which is long-standing size debt behind a ratchet: an
 * allowlisted file may shrink and never grow. These constants are a coherent
 * unit with one reason to change (the rail's geometry), so they move together
 * rather than a row at a time. The reasoning that came with each one is
 * unchanged below — it is the record of what each value was before, and why the
 * previous one was wrong.
 *
 * The contrast measurements these alphas answer to are in the header of
 * `Sidebar.tsx`, beside the rail's own tokens.
 * ======================================================================== */

/**
 * ONE SIZE, ONE WEIGHT — for every glyph on the rail, spread from here.
 *
 * Lucide draws on a 24px grid at `strokeWidth` 2, so the stroke a browser
 * actually paints is `2 × size / 24`: the same icon at 14px lands at 1.17px and
 * at 18px at 1.50px. Size and weight are therefore ONE decision, not two, and
 * the group-disclosure chevron — the single glyph here that was set to 14 —
 * was not a smaller icon so much as a thinner one, sitting in the same row as
 * an 18px section icon. Both numbers are declared, so a future icon cannot
 * quietly inherit a different weight by picking a different size.
 *
 * Spread (`{...NAV_ICON}`) rather than wrapped in a component: `NavLeaf.icon`
 * is a `LucideIcon` and the rail also renders fixed glyphs (chevrons, the close
 * X, the surface-switch arrow), so a spread is the one form that covers both
 * without a second indirection.
 */
export const NAV_ICON = { size: 17, strokeWidth: 2 } as const;

/**
 * The SECTION-HEADING glyph, which is deliberately not `NAV_ICON`.
 *
 * `Hadir Dashboard.dc.html` draws the eyebrow's mark at 13px and stroke 2.4
 * against the rows' 17px at stroke 2 — smaller, and compensating for the loss
 * of weight at that size by drawing heavier. That is the same relationship the
 * eyebrow's TYPE has to a row's label, so the glyph follows the type rather
 * than staying row-sized while the words shrink beside it.
 *
 * This is the one sanctioned second size on the rail. `Sidebar.test.tsx` still
 * pins BOTH tiers so a third cannot appear by accident.
 */
export const HEADING_ICON = { size: 13, strokeWidth: 2.4 } as const;

/**
 * How a row's LABEL behaves once the rail is collapsed to icons.
 *
 * `md:hidden` — what this used to be — is `display: none`, which takes the text
 * out of the accessibility tree along with the pixels. The rows survived that
 * because each carries a `title`, and a title is a last-resort accessible name;
 * the check-in CTA had no title, so the app's primary action became a link with
 * no name at all the moment somebody collapsed the rail.
 *
 * `sr-only` clips the label to a 1px box instead. It is out of flow (position:
 * absolute), so it costs no width and contributes no flex gap — the row looks
 * identical — but it is still text, so every destination keeps a real
 * accessible name in every state, from one rule rather than per-row `aria-label`
 * patches. Only `md:` is touched: the mobile drawer is never collapsed.
 */
export const COLLAPSED_LABEL = collapsedClasses(false).label; // every width on the back office: railScope.ts

/** Divider/hairline on the rail — under the rail head, and above every group.
 * It draws the group separators at every width now, not only while the rail is
 * collapsed: with the section heading demoted to a quiet eyebrow, this line is
 * what says where one section ends.
 *
 * /22 rather than /15: the old value was tuned against a saturated surface, and
 * a hairline that read on violet disappears on ink. Decorative, so no contrast
 * floor applies — it just has to be seen. */
/* 22 is not a step on Tailwind's opacity scale and bare numbers are not
 * arbitrary values, so `/22` compiled to NOTHING and the divider never drew.
 * Compile-probed against the real config: `/20` and `/[22%]` both emit, `/22`
 * does not. Taking `/20` — the scale step — rather than pinning an arbitrary
 * 22% nobody would have missed. */
export const RAIL_DIVIDER = 'border-nav-border';

/**
 * Focus ring for anything sitting ON the rail, drawn in the paired ink so it
 * stays visible in both themes and for any tenant brand.
 *
 * The 2px offset used to be the risky part — on a full-bleed row it had nowhere
 * to go but into the neighbouring row. Rows are inset pills now, so the ring has
 * the rail's own 12px of inline room to sit in and it traces each pill's corner
 * radius, which is the shape a focused row actually has.
 *
 * `outline-nav-active`, NOT the canon `outline-ring`, and this is a fix rather
 * than a preference. `--ring` is declared equal to `--primary`, and `--primary`
 * in light theme is #5323e6 against a #1B1A21 rail: 2.26:1, under the 3:1 WCAG
 * 2.4.11 asks of a focus indicator. Every keyboard user tabbing the rail in the
 * app's default theme was aiming at an outline they could not see, and the
 * comment at the top of Sidebar.tsx had said as much ("`--ring` is `--primary`,
 * which is far too dark to see on this rail") while the code went on using it.
 * `--nav-active` is the brand LIFTED until it reads on ink — 5.90:1 light /
 * 6.40:1 dark on the bare rail, and 4.31 / 5.13 over the current row's own
 * ground, which is the hardest place a rail focus ring lands. It is also the
 * token the tenant's brand already re-seeds, so the ring is their colour too.
 *
 * It is stated ONCE. The admin group's disclosure button carried its own
 * hand-written copy of this ring in `outline-nav-active` — the right colour by
 * accident, and a second focus dialect on a rail that is supposed to have one
 * (§6). That copy is gone; the button takes this.
 */
export const RAIL_FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-active';

/**
 * THE RAIL'S PALETTE, HANDED TO THE APP'S ICON-BUTTON PRIMITIVE.
 *
 * This used to be a whole control: a `cn()` of radius, target floor, transition,
 * hover ramp and focus ring, dropped onto a bare `<button>` at two call sites
 * (the collapse chevron and the old drawer's close X). That is §6's "hand-roll a
 * primitive you could import", and it had already cost the two things that
 * always go missing when a control is spelled out rather than imported: no
 * press state, and a size that came from whatever the icon happened to be
 * rather than from a step. `IconButton` owns all of it — square by
 * construction, `.state-layer` press ramp, the coarse-pointer floor, and a
 * REQUIRED `label` that becomes both the accessible name and the tooltip, which
 * is the property the collapsed rail cannot lose.
 *
 * What is left here is the part a primitive built for the warm page cannot
 * know: its `ghost` variant inks itself `text-muted-foreground` →
 * `text-foreground`, and `--foreground` IS the rail's own colour in light
 * theme (1.00:1). So the two ink levels are re-pointed at the rail's ramp and
 * the hover ground at the rail's own — the same brand-mixed ground every row
 * uses, so the head's control and the rows under it light up the same way.
 *
 * `size="sm"` (32px) is the step nearest the handoff's 28px tile; the coarse
 * floor inside the primitive still raises it to 44px on a touch tablet, which
 * is where that rule was always doing its work.
 */
export const RAIL_ICON_BUTTON = cn(
  'text-nav-on-surface-muted hover:bg-nav-hover-surface hover:text-nav-on-surface',
  RAIL_FOCUS
);

/**
 * THE RAIL'S OWN INSET, and why rows are pills now.
 *
 * Rows used to be full-bleed — edge to edge, no radius — with a 2px logical
 * border on the inline-start side that was transparent until the row was
 * current. That was the right shape for a 240px rail whose active row had to be
 * found in a saturated slab. The reskin gives the rail 12px of inline breathing
 * room and rounds each row to `--radius-control`, so a row reads as an object
 * you can point at rather than a band across the whole column, and the current
 * one is a filled pill instead of a full-width stripe.
 *
 * The inset lives on the ONE scrolling container rather than on each row, group
 * and pill wrapper, so the rail has a single answer to "how far in does a row
 * start" — and the group hairlines inset with it, which is what makes them read
 * as dividers inside the rail instead of scars across it.
 *
 * Collapsed, it shrinks to 4px: at 52px wide, 12px either side of a 44px target
 * leaves 28px, and the glyph stops being centred on anything.
 */
// 14px, the handoff's own rail inset (`padding: 18px 14px 14px` on the nav,
// `Hadir Dashboard.dc.html:32`). It was `ps-3 pe-3` — 12px — which pulled every
// row, pill and hairline 2px tighter to the edge than the design draws them.
export const RAIL_INSET = 'ps-3.5 pe-3.5';

/**
 * Row geometry, shared by every destination on the rail: an inset pill at
 * `--radius-control`, 44px minimum height, and `relative` so the current row can
 * carry its marker (see `ActiveMarker`) without the marker taking part in
 * layout — nothing shifts when the highlight moves. `ps`/`pe` throughout, never
 * `pl`/`pr`: this ships in Arabic.
 *
 * `collapsed` is a DESKTOP-only state. The mobile drawer is always full width,
 * so collapse is expressed purely through `md:` classes and the same DOM serves
 * both — no second render path, no behaviour that differs by viewport.
 *
 * THREE SIGNALS mark the current row, because the rail has two shapes and the
 * label is only present in one of them: the brand bar on the inline-start edge,
 * a lifted background, and the glyph itself in `--nav-active` (see `Leaf`). The
 * heavier label is a fourth — and it is the one that disappears when the rail
 * collapses to 52px, which is why it cannot be the only one.
 *
 * HOVER AND THE CURRENT ROW ARE NO LONGER THE SAME RECTANGLE, and they are no
 * longer neutral. Both were `bg-nav-on-surface/10` — one grey wash doing two
 * jobs — which meant the rail's answer to "where am I" and its answer to "what
 * is under the pointer" were pixel-identical, and neither of them was the
 * tenant's colour. The old note here defended that on the grounds that the
 * handoff separates the two by two percent and the opacity scale cannot express
 * it. True, and beside the point: the fix was never a third alpha, it was a
 * ground with a HUE in it. `--nav-hover-surface` and `--nav-active-surface`
 * are the brand mixed into the rail at 10% and 20% (see index.css), so the two
 * states differ by a factor of two in the same colour — the same thing, less of
 * it — and both say whose product this is.
 *
 * The current row keeps all four signals it had; only the ground's colour
 * changed, and it lifts off the rail slightly harder than the wash it replaces
 * (1.37:1 against 1.34:1), so nothing about finding your place got quieter.
 */
export function rowClasses(opts: { collapsed: boolean; active?: boolean; everyWidth?: boolean }): string {
  return cn(
    // `[@media(pointer:coarse)]`, not a blanket `min-h-touch` — see the note
    // on `RAIL_ICON_BUTTON`. Forty-odd rows each carrying a mouse-only
    // tablet's touch floor is what made the whole rail read visibly looser
    // than the handoff; `py-2.5` (10px) alone gives a fine pointer the
    // handoff's own ~35px row, and a coarse pointer still gets 44px.
    // `py-[9px]` is the handoff's own row padding (`padding: 9px 10px`), which
    // with a 17px glyph and 13.5px label lands the ~37px row it draws. `py-2.5`
    // (10px) was a whole step of the spacing scale wider on every one of forty
    // rows, which is most of why the rail read looser than the mock.
    'relative flex [@media(pointer:coarse)]:min-h-touch w-full items-center gap-2.5 rounded-control py-rail-row',
    'ps-2.5 pe-2.5 text-start text-copy',
    'transition-colors duration-fast',
    RAIL_FOCUS,
    opts.collapsed && collapsedClasses(opts.everyWidth ?? false).row,
    opts.active
      ? 'bg-nav-active-surface font-semibold text-nav-on-surface'
      : 'font-medium text-nav-on-surface-muted hover:bg-nav-hover-surface hover:text-nav-on-surface'
  );
}

/**
 * THE SECTION EYEBROW — type, colour and inset, shared by both kinds of heading
 * the rail draws.
 *
 * 12px uppercase at +0.3px tracking (`text-eyebrow`), the one step in the system
 * allowed to be uppercase and the smallest word this app may set: §1 of
 * docs/design-rules.md bans 11px for anything read as a word, which is what the
 * handoff sets these at. `/60` against rows at `/80`, so a heading is audibly
 * quieter than the destinations under it — see the contrast note at the top.
 *
 * GEOMETRY IS NOT SHARED, deliberately. The admin sections collapse, so their
 * heading is a button and answers to the 44px target floor; the personal
 * heading names a list that does not collapse, so it is text and a 44px text
 * box would be a control-sized thing nothing happens when you press. One voice,
 * two shapes.
 */
/* Geometry is the handoff's: `gap: 8px; padding: 0 8px 6px` — no space above
   the heading at all (the group's own `pt` already put it there) and 6px under
   it, so the words sit tight to the rows they name. It was `ps-2.5 pe-2.5`
   plus a `py-2` at each call site, which made a 34px block for a 12px word. */
export const EYEBROW = 'flex items-center gap-element rounded-control ps-2 pe-2 pb-1.5 text-eyebrow';
